/**
 * EditIncidentModal — focused edit modal for an HSE incident (DEV-822 / DEV-829).
 *
 * Reached by clicking an incident row in the IncidentDrillDown panel on
 * an HSE card. Lets the user update the fields that change during
 * investigation lifecycle:
 *   - Title (correction)
 *   - Description (more detail as investigation proceeds)
 *   - Status (open → in_progress → resolved → closed)
 *   - Severity (correction or escalation)
 *   - Confidential (visibility control)
 *
 * Read-only by intentional design:
 *   - Incident date — immutable per spec § 5.2 (safety record integrity)
 *   - HSE category — locked to prevent breaking the 1-per-category invariant
 *
 * Lighter than IssueDrawer (which needs role slot lookups, user resolvers,
 * full risks/issues integration). For full edit + ownership management,
 * the standard Risks & Issues tab still works.
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 7.4
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchProjectIssue,
  updateProjectIssue,
  deleteProjectIssue,
  fetchMiniProjectHseIncident,
  updateMiniProjectHseIncident,
  deleteMiniProjectHseIncident,
  type ProjectIssueResponse,
  type UpdateProjectIssueRequest,
} from '../../api/projectIssues';
import { invalidateByPrefix, invalidateAndRefetch } from '../../api/queryCache';
import { isUserSystemAdmin } from '../../security/securityAdapter';
import {
  severitiesForCategory,
  severityIsRed,
  severityIsYellow,
} from '../../kpi-engine/hse';
import type { HseCategory, HseSeverity } from '../../kpi-engine/missionTypes/types';
import { AttachmentPicker } from '../AttachmentPicker/AttachmentPicker';
import styles from './EditIncidentModal.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface EditIncidentModalProps {
  /** Issue id to load. When null/undefined the modal is closed. */
  issueId: string | null;
  projectGuid: string;
  /**
   * DEV-1141. When set, reads/writes route to the mini-project HSE incident
   * endpoints instead of the full-project issues route. FP behavior is
   * unchanged when omitted.
   */
  miniProjectGuid?: string;
  /** Project name for the breadcrumb context confirmation. */
  projectName?: string;
  onClose: () => void;
  /** Called after a successful save. Parent may want to refresh lists. */
  onSaved?: (issueId: string) => void;
  /**
   * DEV-847: Current user guid. Used to determine whether to show the Delete
   * affordance — system admins only per DEV-832's HSE permission spec
   * (incidents are regulatory records; deletion is intentionally a higher
   * bar than edit). Backend enforces the same rule; this just hides the
   * button for users who'd get a 403 anyway.
   */
  currentUserGuid?: string;
  /**
   * Called after a successful delete so the parent can clear caches and
   * close the modal. Receives the deleted issue id.
   */
  onDeleted?: (issueId: string) => void;
}

// ── Status options ───────────────────────────────────────────────

const STATUSES = ['open', 'in_progress', 'mitigated', 'closed', 'reopened'] as const;
type IssueStatus = (typeof STATUSES)[number];

// ── Helpers ──────────────────────────────────────────────────────

function severityColor(
  severity: HseSeverity,
  category: HseCategory,
): 'yellow' | 'red' {
  return severityIsRed(severity, category)
    ? 'red'
    : severityIsYellow(severity, category)
    ? 'yellow'
    : 'yellow';
}

function formatIncidentDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ── Component ────────────────────────────────────────────────────

export function EditIncidentModal({
  issueId,
  projectGuid,
  miniProjectGuid,
  projectName,
  onClose,
  onSaved,
  currentUserGuid,
  onDeleted,
}: EditIncidentModalProps) {
  const { t } = useAppTranslation('kpi');
  const { t: tCommon } = useAppTranslation('common');

  const isMini = !!miniProjectGuid;
  // Owner used for file-attachment namespacing (FileUpload entityId).
  const ownerGuid = miniProjectGuid || projectGuid;

  const [issue, setIssue] = useState<ProjectIssueResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields — initialized from the loaded issue, mutated locally.
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<IssueStatus>('open');
  const [severityHse, setSeverityHse] = useState<HseSeverity>('first-aid');
  const [confidential, setConfidential] = useState(false);
  // DEV-822 #126: editable attachment list. Initial value comes from the
  // loaded issue; AttachmentPicker handles upload + remove.
  const [attachmentKeys, setAttachmentKeys] = useState<string[]>([]);

  const open = issueId != null;
  const category = (issue?.hseCategory ?? 'safety') as HseCategory;
  const severityOptions = useMemo(
    () => severitiesForCategory(category),
    [category],
  );

  // Fetch the issue when the modal opens.
  useEffect(() => {
    if (!open || !issueId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // DEV-848: Reset transient submit state on every (re)open. Without this,
    // a previous successful save left `submitting=true` permanently — and
    // since handleClose is gated on `!submitting`, the modal would freeze on
    // reopen (Save button stuck on "Saving…", Close ignored). Resetting here
    // guarantees a clean slate regardless of how the previous instance ended.
    setSubmitting(false);
    (isMini
      ? fetchMiniProjectHseIncident(miniProjectGuid!, issueId)
      : fetchProjectIssue(projectGuid, issueId))
      .then((data) => {
        if (cancelled) return;
        setIssue(data);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setStatus((STATUSES as readonly string[]).includes(data.status) ? (data.status as IssueStatus) : 'open');
        setSeverityHse((data.severityHse as HseSeverity) || 'first-aid');
        setConfidential(data.confidential ?? false);
        setAttachmentKeys(data.attachmentFileKeys ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, issueId, projectGuid, miniProjectGuid, isMini]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setIssue(null);
    setError(null);
    onClose();
  }, [submitting, onClose]);

  const handleBackdropClick: React.MouseEventHandler = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleSave = useCallback(async () => {
    if (!issueId || !issue) return;
    setError(null);

    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();
    if (!trimmedTitle) {
      setError(t('hse.modal.error.titleRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const req: UpdateProjectIssueRequest = {
        title: trimmedTitle,
        description: trimmedDesc,
        status,
        // Map HSE severity to standard severity bucket so non-HSE queries
        // see something coherent (matches ReportIncidentModal's mapping).
        severity:
          severityIsRed(severityHse, category) ? 'blocking' : 'warning',
        severityHse,
        confidential,
        // Persist the current attachment list (DEV-822 #126). Backend
        // replaces the full set; we send all keys including ones that
        // were already attached. Removed items are absent from this list.
        attachmentFileKeys: attachmentKeys,
      };
      const updated = isMini
        ? await updateMiniProjectHseIncident(miniProjectGuid!, issueId, req)
        : await updateProjectIssue(projectGuid, issueId, req);
      /*
       * DEV-849: Properly INVALIDATE caches before notifying subscribers.
       *
       * Previously this code dispatched a CACHE_UPDATE_EVENT raw without
       * actually marking cache entries stale. Subscribers received the event
       * and called refetch(), but `fetchForKey` saw fresh cached data and
       * returned it — so the HSE card kept showing the old severity even
       * though the drill-down list (which fetches via a different code path)
       * showed the new value.
       *
       * `invalidateByPrefix('hse-incidents')` covers BOTH the per-project key
       * (`hse-incidents:<guid>`) AND the bulk-fetch key (`hse-incidents-bulk:...`)
       * that App.tsx uses to populate hse_bundle on every project row. Each
       * affected entry is marked stale, notifySubscribers fires per key, and
       * subscribers' refetches go to the network instead of returning cache.
       */
      invalidateByPrefix(isMini ? 'hse-mp-incidents' : 'hse-incidents');
      invalidateAndRefetch('project_issues');
      onSaved?.(updated.id);
      setIssue(null);
      // DEV-848: Reset submitting BEFORE onClose so the parent can't observe
      // a stale `submitting=true` if it inspects state during the close
      // animation. Was missing entirely on the success path — caused the
      // reopen freeze (handleClose gated on !submitting).
      setSubmitting(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }, [
    issueId,
    issue,
    title,
    description,
    status,
    severityHse,
    confidential,
    attachmentKeys,
    category,
    projectGuid,
    miniProjectGuid,
    isMini,
    onSaved,
    onClose,
    t,
  ]);

  /*
   * DEV-847: Delete handler. Only callable when the current user is a
   * system admin (button is hidden otherwise; backend also enforces).
   * HSE incidents are regulatory records, so:
   *   - Deletion requires explicit confirmation
   *   - Cache invalidation matches handleSave so the HSE card + drill-down
   *     both refresh
   *   - Submitting flag is reset before close (mirrors the DEV-848 freeze fix)
   */
  const canDelete = !!currentUserGuid && isUserSystemAdmin(currentUserGuid as any);
  const handleDelete = useCallback(async () => {
    if (!issueId || !canDelete) return;
    const confirmed = window.confirm(
      t(
        'hse.editModal.confirmDelete',
        'Delete this incident? HSE incidents are part of the safety record. This cannot be undone.',
      ),
    );
    if (!confirmed) return;
    setError(null);
    setSubmitting(true);
    try {
      if (isMini) {
        await deleteMiniProjectHseIncident(miniProjectGuid!, issueId);
      } else {
        await deleteProjectIssue(projectGuid, issueId);
      }
      invalidateByPrefix(isMini ? 'hse-mp-incidents' : 'hse-incidents');
      invalidateAndRefetch('project_issues');
      onDeleted?.(issueId);
      setIssue(null);
      setSubmitting(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }, [issueId, canDelete, projectGuid, miniProjectGuid, isMini, onDeleted, onClose, t]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="hse-edit-modal-title"
    >
      <div className={styles.modal}>
        <div className={styles.head}>
          <div className={styles.headTitleStack}>
            {projectName && (
              <div className={styles.projectBreadcrumb}>
                <span className={styles.projectBreadcrumbLabel}>
                  {t('hse.modal.projectLabel')}
                </span>
                <span className={styles.projectBreadcrumbSep}>·</span>
                <span className={styles.projectBreadcrumbName}>{projectName}</span>
              </div>
            )}
            <h3 id="hse-edit-modal-title" className={styles.title}>
              {t('hse.editModal.title', 'Edit incident')} —{' '}
              <span className={styles.titleCategory}>
                {t(`hse.category.${category}`)}
              </span>
            </h3>
          </div>
          <button
            className={styles.closeBtn}
            onClick={handleClose}
            type="button"
            aria-label={tCommon('close')}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {loading && (
            <div className={styles.loading}>
              {t('hse.editModal.loading', 'Loading incident…')}
            </div>
          )}

          {!loading && issue && (
            <>
              {/* Read-only context: incident date + category */}
              <div className={styles.readOnlyRow}>
                <div className={styles.readOnlyField}>
                  <label className={styles.fieldLabel}>
                    {t('hse.modal.field.incidentDate')}
                  </label>
                  <div className={styles.readOnlyValue}>
                    {formatIncidentDate(issue.incidentDate)}{' '}
                    <span className={styles.lockHint}>
                      🔒 {t('hse.editModal.dateLocked', 'immutable')}
                    </span>
                  </div>
                </div>
                <div className={styles.readOnlyField}>
                  <label className={styles.fieldLabel}>
                    {t('hse.modal.field.category')}
                  </label>
                  <div className={styles.readOnlyValue}>
                    {t(`hse.category.${category}`)}
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t('hse.modal.field.title')}
                  <span className={styles.req}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={500}
                />
              </div>

              {/* Description */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t('hse.modal.field.description')}
                </label>
                <textarea
                  className={styles.input}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  maxLength={4000}
                />
                <div className={styles.fieldHelp}>
                  {t(
                    'hse.editModal.descriptionHelp',
                    'Update the description as the investigation proceeds.',
                  )}
                </div>
              </div>

              {/* Status — investigation lifecycle */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t('hse.editModal.statusLabel', 'Investigation status')}
                </label>
                <select
                  className={styles.input}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as IssueStatus)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`hse.drillDown.status.${s}`, s)}
                    </option>
                  ))}
                </select>
                <div className={styles.fieldHelp}>
                  {t(
                    'hse.editModal.statusHelp',
                    'Closing an investigation does NOT reset the streak counter — the incident date is immutable.',
                  )}
                </div>
              </div>

              {/* Severity (HSE) — chip picker, category-aware */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t('hse.modal.field.severity')}
                </label>
                <div className={styles.severityGrid}>
                  {severityOptions.map((sev) => {
                    const color = severityColor(sev, category);
                    return (
                      <button
                        key={sev}
                        type="button"
                        className={`${styles.sevChip} ${
                          severityHse === sev ? styles.sevChipSelected : ''
                        }`}
                        data-severity={sev}
                        data-color={color}
                        onClick={() => setSeverityHse(sev)}
                      >
                        <span className={styles.sevChipLabel}>
                          {t(`hse.severity.${sev}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Attachments (DEV-822 #126). Existing attachments load
                  from the issue; user can add or remove freely. Save
                  persists the full list via PATCH. */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t('hse.modal.field.attachments', 'Attachments')}
                </label>
                <AttachmentPicker
                  value={attachmentKeys}
                  onChange={setAttachmentKeys}
                  category="incidents"
                  entityId={ownerGuid}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
              </div>

              {/* Confidential */}
              <div className={styles.field}>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={confidential}
                    onChange={(e) => setConfidential(e.target.checked)}
                  />
                  <span className={styles.checkboxBody}>
                    <strong>{t('hse.modal.field.confidential')}</strong>
                    <span className={styles.checkboxDesc}>
                      {t('hse.modal.help.confidential')}
                    </span>
                  </span>
                </label>
              </div>

              {error && (
                <div className={styles.errorBanner} role="alert">
                  {error}
                </div>
              )}
            </>
          )}

          {!loading && !issue && error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <div className={styles.footNote}>
            {issue?.id && (
              <>
                {t('hse.editModal.issueId', 'Issue')}:{' '}
                <code>{issue.id.slice(0, 8)}</code>
              </>
            )}
          </div>
          <div className={styles.footActions}>
            {/* DEV-847: System-admin-only Delete affordance for HSE incidents.
                Sits LEFT of Cancel/Save with destructive styling so it reads
                as a separate, deliberate action — not a default. The backend
                enforces the same permission gate (DEV-832), so users without
                rights would just get a 403 if they hit it; hiding the button
                avoids that confusion. */}
            {canDelete && (
              <button
                className={styles.btn}
                type="button"
                onClick={handleDelete}
                disabled={submitting || loading || !issue}
                style={{
                  marginInlineEnd: 'auto',
                  color: 'var(--color-error)',
                  borderColor: 'var(--color-error)',
                }}
                title={t('hse.editModal.deleteHint', 'Delete this incident (system admin only)')}
              >
                {t('hse.editModal.delete', 'Delete')}
              </button>
            )}
            <button
              className={styles.btn}
              type="button"
              onClick={handleClose}
              disabled={submitting}
            >
              {tCommon('cancel')}
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              type="button"
              onClick={handleSave}
              disabled={submitting || loading || !issue}
            >
              {submitting
                ? t('hse.modal.saving')
                : t('hse.editModal.save', 'Save changes')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default EditIncidentModal;
